import { useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import CustomModal from '@/components/shared/CustomModal/CustomModal'
import ModalBody from '@/components/shared/CustomModal/ModalBody'
import ModalFooter from '@/components/shared/CustomModal/ModalFooter'
import ModalHeader from '@/components/shared/CustomModal/ModalHeader'
import { useEffect, useMemo, useState } from 'react'
import { omit } from '@/utils'

import './MergePartiesModal.css'
import { toast } from 'react-toastify'
import { MdSearch } from 'react-icons/md'

const MergePartiesModal = ({ runsheetId, titleBreakdownId, handleClose, selectedParty }) => {
    
    const uniqueParties = useWireValue(store.uniqueParties)
    const [isSaving, setIsSaving] = useState(false)
    const [currentParty, setCurrentParty] = useState({})
    const [markedParties, setMarkedParties] = useState([])
    const [displayedParties, setDisplayedParties] = useState([])
    const [search, setSearch] = useState('')
    
    const parties = useMemo(() => {
        return uniqueParties?.reduce((acc, curr) => {
            curr.parties.forEach(p => {
                if (!acc.some(v => v.id === p.id))
                    acc.push(p)
            })
            return acc
        }, [])
    }, [uniqueParties])
    
    useEffect(() => {
        const currParty = { ...(uniqueParties?.find(up => up.id === selectedParty) || {}) }
        
        setCurrentParty(currParty)
        setMarkedParties(currParty.parties?.map(p => p.id) || [])
        setSearch('')
        
    }, [uniqueParties, selectedParty])
    
    useEffect(() => {
        if (parties) {
            const filtered = search
                ? parties.filter(p => p.name.toLowerCase().includes(search.toLowerCase()))
                : [...parties]
            
            const currentPartyIds = currentParty?.parties?.map(p => p.id) || []
            
            // sort by other stuff too, like levenshtein distance
            setDisplayedParties(filtered.sort((a, b) => {
                if (currentPartyIds.includes(a.id) && !currentPartyIds.includes(b.id))
                    return -1
                else if (!currentPartyIds.includes(a.id) && currentPartyIds.includes(b.id))
                    return 1
                return a.name.localeCompare(b.name)
            }))
        }
    }, [parties, currentParty, selectedParty, search])
    
    const toggleMarkedParty = id => {
        if (markedParties.includes(id)) {
            setMarkedParties(markedParties.filter(mp => mp !== id))
        } else {
            setMarkedParties([...markedParties, id])
        }
    }
    
    const updateAndMerge = async () => {
        setIsSaving(true)
        
        try {
            
            // add the removed parties
            const partyIds = currentParty.parties
                .filter(p => !markedParties.includes(p.id))
                .map(p => ({ id: p.id, state: 'remove' }))
            
            // remove the already existing party and add the state for the new others
            partyIds.push(...markedParties
                .filter(id => !currentParty.parties.some(p => p.id === id))
                .map(id => ({ id, state: 'add' })))
            
            await actions.mergeUniqueParties(runsheetId, {
                titleBreakdownId,
                uniqueParty: omit(currentParty, ['party']),
                partyIds,
            })
            handleClose(true)
        } catch (err) {
            console.log(err)
            toast.error(err?.response?.data?.message || 'Failed to Merge Parties')
        } finally {
            setIsSaving(false)
        }
    }
    
    return (
        <CustomModal open={!!selectedParty} modalHandler={() => handleClose(false)}>
            <ModalHeader>Party Name Merge</ModalHeader>
            <ModalBody>
                <div className="flex flex-col gap-2">
                    <label className="form-control flex-1">
                        <span className="label-text">Unique Party Name</span>
                        <input type="text" value={currentParty?.simpleName || ''}
                            onChange={e => 
                                setCurrentParty({ ...currentParty, simpleName: e.target.value })}
                            className="input input-bordered w-full" />
                    </label>
                </div>
                <div className="divider my-2"></div>
                <div className="flex justify-between">
                    <p>Parties to merge</p>
                    <label className="input input-xs input-bordered flex items-center gap-2">
                        <MdSearch />
                        <input className="border-none" type="text" placeholder="Search" value={search}
                            onChange={e => setSearch(e.target.value)} />
                    </label>
                </div>
                <div className="max-h-[500px] overflow-auto">
                    <ul className="menu bg-base-100 rounded my-2">
                        {displayedParties
                            ?.map(up =>
                                <li key={up.id}>
                                    <div className="flex justify-between !cursor-default">
                                        <label className="label flex justify-start gap-2 cursor-pointer p-0">
                                            <input type="checkbox"
                                                className="checkbox checkbox-primary"
                                                checked={markedParties.includes(up.id)}
                                                onChange={() => toggleMarkedParty(up.id)} />
                                            <span className="label-text flex-1">{up.name}</span>
                                        </label>
                                    </div>
                                </li>,
                            )
                        }
                    </ul>
                </div>
            </ModalBody>
            <ModalFooter>
                <button className="btn btn-outline btn-primary" onClick={() => handleClose(false)}>Cancel</button>
                <button
                    className="btn btn-primary"
                    onClick={updateAndMerge}
                    disabled={isSaving || !currentParty?.simpleName}>Save</button>
            </ModalFooter>
        </CustomModal>
    )
}

export default MergePartiesModal